import { Gradient } from '@/components/gradient'
import { RestaurantDealPreview } from '@/features/restaurants/restaurant-deal-preview'
import { IChildrenProps } from '@/typings/children-props'
import { cn } from '@/utils/styles/cn'
import { RestaurantFlashDealPreview } from '../restaurant-flash-deal-preview'
import { RestaurantRewardPreview } from '../restaurant-reward-preview'

const MAX_ON_IMAGE_CHIPS = 2

interface IRestaurantCardDealsProps {
    restaurant: {
        deals?: Array<{ name: string | null; eventDeal?: boolean | null }> | null
        uuid: string
        loyalty?: { rewards?: Array<{ name: string }> } | null
    }
    /** Frosted glass chips for use on the photo gradient instead of the solid pills */
    onImage?: boolean
    className?: string
}

/** Frosted chip carrying the deal semantics as tint and text color */
const FrostedChip = ({
    children,
    className,
    icon,
}: IChildrenProps & { className?: string; icon?: React.ReactNode }) => {
    return (
        <div
            className={cn(
                `flex min-h-8 max-h-8 w-fit items-center gap-x-1 whitespace-nowrap rounded-full
                px-3 text-label-sm backdrop-blur-md`,
                className
            )}
        >
            {icon}
            <span>{children}</span>
        </div>
    )
}

export const RestaurantCardDeals = ({
    restaurant,
    onImage = false,
    className,
}: IRestaurantCardDealsProps) => {
    if (!restaurant.deals?.length && !restaurant.loyalty?.rewards?.length) return null

    if (onImage) {
        const chips = [
            ...(restaurant.deals ?? []).map((deal) => ({
                label: deal.name,
                isReward: false,
                isFlashDeal: Boolean(deal.eventDeal),
            })),
            ...(restaurant.loyalty?.rewards ?? []).map((reward) => ({
                label: reward.name,
                isReward: true,
                isFlashDeal: false,
            })),
        ]

        // Two chips carry the message, the rest is one click away on the
        // detail page: no scrolling row, no truncated labels
        const shownChips = chips.slice(0, MAX_ON_IMAGE_CHIPS)
        const hiddenCount = chips.length - shownChips.length

        return (
            <div className={cn('flex items-center gap-2 overflow-hidden', className)}>
                {shownChips.map((chip, index) => (
                    <FrostedChip
                        key={'restaurant-card' + restaurant.uuid + chip.label + index}
                        className={cn(
                            'min-w-0 overflow-hidden text-ellipsis',
                            chip.isReward
                                ? 'bg-bg-accent-01/20 text-bg-accent-01-strong'
                                : 'bg-bg-overlay-brand text-fg-brand'
                        )}
                        icon={
                            chip.isFlashDeal ? (
                                <img
                                    src="/_images/big-icons/bolt.svg"
                                    alt=""
                                    aria-hidden
                                    className="h-[1em] w-[1em]"
                                />
                            ) : undefined
                        }
                    >
                        {chip.label}
                    </FrostedChip>
                ))}

                {hiddenCount > 0 && (
                    <FrostedChip className="bg-fg-inverse/15 text-fg-inverse shrink-0">
                        +{hiddenCount}
                    </FrostedChip>
                )}
            </div>
        )
    }

    return (
        <div className="relative overflow-hidden">
            <div
                className={cn(
                    `flex flex-1 mt-2 items-center justify-start overflow-x-auto gap-2 whitespace-nowrap relative w-full
                    min-h-4 min-w-full flex-grow-0 flex-shrink-0 scrollbar-hide`,
                    className
                )}
            >
                {restaurant.deals?.map((deal, index) => {
                    if (deal.eventDeal)
                        return (
                            <RestaurantFlashDealPreview
                                key={'restaurant-card' + restaurant.uuid + deal.name + index}
                            >
                                {deal.name}
                            </RestaurantFlashDealPreview>
                        )
                    return (
                        <RestaurantDealPreview
                            key={'restaurant-card' + restaurant.uuid + deal.name + index}
                        >
                            {deal.name}
                        </RestaurantDealPreview>
                    )
                })}

                {restaurant.loyalty?.rewards?.map((reward, index) => {
                    return (
                        <RestaurantRewardPreview
                            key={'restaurant-card' + restaurant.uuid + reward.name + index}
                        >
                            {reward.name}
                        </RestaurantRewardPreview>
                    )
                })}

                <div className="w-20" />
            </div>

            <Gradient className="absolute right-0 top-0 min-w-[20px]" />
        </div>
    )
}
